import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { format } from "date-fns"
import hljs from "highlight.js/lib/core"
import json from "highlight.js/lib/languages/json"
import { AnimatePresence, motion } from "motion/react"
import {
	IconCopyOutline18 as Copy,
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeOff,
	IconPencilOutline18 as Pencil,
	IconToggleOutline18 as ToggleLeft,
	IconToggle2Outline18 as ToggleRight,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"

import type { User, Webhook, WebhookDelivery } from "@productlane/zero"
import {
	AlertDialog,
	CopyButton,
	Dot,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	Input,
	Loader,
	Pill,
	Tooltip,
} from "@productlane/ui"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { queries } from "@productlane/zero/queries"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsHeader } from "../../../settings-header"
import { SettingsContainer } from "../../../settings-switches/settings-container"
import { SettingsSection } from "../../../settings-switches/settings-section"
import { WEBHOOK_LIST_PATH, webhookEditPath } from "./shared"

// Webhook payloads are always JSON, and response bodies are highlighted
// as JSON best-effort. Registering only the one grammar keeps the
// bundle from pulling all of hljs.
if (!hljs.getLanguage("json")) {
	hljs.registerLanguage("json", json)
}

// Read-only detail page at /settings/integrations/api/webhooks/:id.
// Renders the static config + recent delivery audit log. Mutations
// (edit / enable-disable / delete) live in the more-actions menu;
// rotating the signing secret is on the edit page.
export function WebhookViewPage() {
	const navigate = useNavigate()
	const { id } = useParams({ strict: false })
	const [hooks] = useQuery(queries.webhooks())
	const hook = useMemo(
		() => hooks.find((h) => h.id === id) ?? null,
		[hooks, id],
	)

	const back = () => void navigate({ href: WEBHOOK_LIST_PATH })

	if (!hook) {
		return (
			<>
				<MetaInfo title="Webhook" />
				<SettingsHeader title="Webhook" subtitle="Loading…" />
				<SettingsContainer>
					<div className="flex items-center justify-center py-8">
						<Loader />
					</div>
				</SettingsContainer>
			</>
		)
	}

	return <WebhookView hook={hook} onBack={back} />
}

// `createdBy` comes from the Zero `webhooks` query's `.related(...)`
// call. Older webhooks may have a null user (the column was added
// after some rows existed).
type WebhookWithCreator = Webhook & { createdBy?: User | null | undefined }

function WebhookView({
	hook,
	onBack,
}: {
	hook: WebhookWithCreator
	onBack: () => void
}) {
	const navigate = useNavigate()
	const ctx = api.useUtils()
	const [revealed, setRevealed] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)

	const updateMutation = api.workspace.updateWebhook.useMutation({
		onSuccess: () => ctx.workspace.invalidate(),
	})
	const deleteMutation = api.workspace.deleteWebhook.useMutation({
		onSuccess: () => {
			toast.success({ title: "Webhook deleted" })
			void ctx.workspace.invalidate()
			onBack()
		},
	})

	const secretQuery = api.workspace.webhookSecret.useQuery(
		{ id: hook.id },
		{ retry: false },
	)
	const secret = secretQuery.data?.secret ?? ""
	const masked = "•".repeat(32)

	const copyValue = async (value: string, kind: string) => {
		await navigator.clipboard.writeText(value)
		toast.success({ title: `${kind} copied` })
	}

	const createdOn = format(new Date(hook.createdAt), "MMM d, yyyy")

	return (
		<>
			<MetaInfo title={hook.label} />
			<SettingsHeader
				title={hook.label}
				subtitle={
					<div className="flex flex-row gap-1 text-sm text-secondary">
						<span className="text-secondary">Created {createdOn}</span>
						{hook.createdBy && (
							<>
								<span className="text-secondary">by</span>
								<div className="flex items-center gap-1.5">
									<span className="text-secondary">
										{hook.createdBy.name ?? hook.createdBy.email}
									</span>
								</div>
							</>
						)}
					</div>
				}
				action={
					<DropdownMenu
						trigger={<IconButton Icon={MoreHorizontal} variant="ghost" />}
					>
						<DropdownMenuItem
							onClick={() => void navigate({ href: webhookEditPath(hook.id) })}
							Icon={Pencil}
						>
							Edit
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() =>
								updateMutation.mutate({
									id: hook.id,
									active: !hook.active,
								})
							}
							Icon={hook.active ? ToggleLeft : ToggleRight}
						>
							{hook.active ? "Disable" : "Enable"}
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => setConfirmDelete(true)}
							Icon={Trash2}
						>
							Delete
						</DropdownMenuItem>
					</DropdownMenu>
				}
			/>

			<SettingsContainer>
				<div className="flex flex-col gap-5">
					{/* Status row */}
					<div className="flex flex-col items-start gap-2 text-sm">
						<div className="">Status</div>
						<div className="flex flex-row items-center gap-1 text-xs text-secondary">
							{hook.active ? (
								<>
									<Dot size="sm" color="green" />
									<span>Active</span>
								</>
							) : (
								<>
									<Dot size="sm" color="gray" />
									<span>Disabled</span>
								</>
							)}
						</div>
					</div>

					<Input
						name="webhook-url"
						label="URL"
						value={hook.url}
						readOnly
						suffix={
							<Tooltip label="Copy URL">
								<IconButton
									Icon={Copy}
									variant="ghost"
									onClick={() => void copyValue(hook.url, "URL")}
								/>
							</Tooltip>
						}
					/>

					<Input
						name="webhook-secret"
						label="Signing secret"
						type="text"
						value={revealed && secret ? secret : masked}
						readOnly
						hint="Used to sign HMAC-SHA256 of every payload. Rotate it on the edit page if it leaks."
						suffix={
							<div className="flex items-center gap-1">
								<Tooltip label={revealed ? "Hide secret" : "Reveal secret"}>
									<IconButton
										Icon={revealed ? EyeOff : Eye}
										variant="ghost"
										onClick={() => setRevealed(!revealed)}
									/>
								</Tooltip>
								<Tooltip label="Copy secret">
									<IconButton
										Icon={Copy}
										variant="ghost"
										onClick={() => void copyValue(secret, "Secret")}
										disabled={!secret}
									/>
								</Tooltip>
							</div>
						}
					/>

					<div>
						<div className="mb-2 text-sm font-medium">
							Subscribed events ({hook.events.length})
						</div>
						<div className="flex flex-wrap gap-1.5">
							{hook.events.length === 0 ? (
								<span className="text-xs text-secondary">No events</span>
							) : (
								hook.events.map((event) => (
									<Pill key={event} variant="dark-gray" size="sm">
										<span className="font-mono">{event}</span>
									</Pill>
								))
							)}
						</div>
					</div>
				</div>
			</SettingsContainer>

			<DeliveriesBlock webhookId={hook.id} />

			<AlertDialog
				isOpen={confirmDelete}
				onOpenChange={setConfirmDelete}
				title="Delete webhook?"
				description={`"${hook.label}" will stop receiving events. This cannot be undone.`}
				confirmText="Delete"
				onCancel={() => setConfirmDelete(false)}
				onConfirm={() => {
					deleteMutation.mutate({ id: hook.id })
					setConfirmDelete(false)
				}}
			/>
		</>
	)
}

// ============================================================
// Deliveries
// ============================================================

function DeliveriesBlock({ webhookId }: { webhookId: string }) {
	const [deliveries] = useQuery(queries.webhookDeliveries({ webhookId }))

	return (
		<>
			<SettingsSection
				title="Recent deliveries"
				description="The last 200 events sent to this webhook."
			/>
			<SettingsContainer className="mt-4 px-0 py-0">
				{deliveries.length === 0 ? (
					<div className="px-4 py-4 text-sm text-secondary">
						No deliveries yet. Trigger an event to see one here.
					</div>
				) : (
					<div className="flex flex-col">
						{deliveries.map((d) => (
							<DeliveryRow key={d.id} delivery={d} />
						))}
					</div>
				)}
			</SettingsContainer>
		</>
	)
}

function DeliveryRow({ delivery }: { delivery: WebhookDelivery }) {
	const [expanded, setExpanded] = useState(false)
	const time = format(new Date(delivery.createdAt), "MMM d, HH:mm:ss")

	return (
		<div className="group/row overflow-hidden border-b first:rounded-t-lg last:rounded-b-lg last:border-b-0">
			<button
				type="button"
				onClick={() => setExpanded(!expanded)}
				className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 group-first/row:rounded-t-lg group-last/row:rounded-b-lg dark:hover:bg-gray-900"
			>
				<DeliveryStatusDot status={delivery.status} />
				<div className="min-w-0 flex-1">
					<div className="flex items-center justify-between gap-2 text-sm">
						<span className="truncate font-mono">{delivery.eventType}</span>
						{delivery.responseStatus !== undefined &&
							delivery.responseStatus !== null && (
								<span
									className={`font-mono text-xs ${
										delivery.responseStatus >= 200 &&
										delivery.responseStatus < 300
											? "text-green-600"
											: "text-red-600"
									}`}
								>
									{delivery.responseStatus}
								</span>
							)}
					</div>
					<div className="text-xs text-secondary">
						{time}
						{delivery.attemptCount > 1 && (
							<> · attempt {delivery.attemptCount}</>
						)}
						{delivery.durationMs !== undefined &&
							delivery.durationMs !== null && <> · {delivery.durationMs}ms</>}
					</div>
				</div>
			</button>
			<AnimatePresence initial={false}>
				{expanded && (
					<motion.div
						key="content"
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{
							height: { duration: 0.22, ease: [0.32, 0.72, 0, 1] },
							opacity: { duration: 0.18, ease: "easeOut" },
						}}
						className="overflow-hidden"
					>
						<div className="space-y-5 border-t bg-gray-50 p-5 dark:bg-gray-900">
							<DeliveryField label="Event ID">
								<span className="break-all font-mono text-xs">
									{delivery.eventId}
								</span>
							</DeliveryField>
							<DeliveryField label="Payload">
								<JsonBlock value={delivery.payload} />
							</DeliveryField>
							{delivery.responseBody && (
								<DeliveryField label="Response body">
									<JsonBlock value={delivery.responseBody} />
								</DeliveryField>
							)}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}

function DeliveryField({
	label,
	children,
}: {
	label: string
	children: ReactNode
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<div className="text-xs text-secondary">{label}</div>
			{children}
		</div>
	)
}

// Renders a JSON code block with hljs syntax highlighting and a copy
// button in the top-right corner. Accepts a structured value (which is
// pretty-printed) or a string (parsed and pretty-printed when it
// happens to be JSON, otherwise rendered raw; still highlighted as
// JSON, since virtually all webhook receivers respond with JSON).
function JsonBlock({ value }: { value: unknown }) {
	const formatted = useMemo(() => {
		if (typeof value === "string") {
			try {
				return JSON.stringify(JSON.parse(value), null, 2)
			} catch {
				return value
			}
		}
		return JSON.stringify(value, null, 2)
	}, [value])

	const html = useMemo(
		() => hljs.highlight(formatted, { language: "json" }).value,
		[formatted],
	)

	return (
		<div className="group/code relative overflow-hidden rounded-md ring-1 ring-black/5 dark:ring-white/10">
			<div className="absolute right-2 top-2 z-10 opacity-0 transition-opacity group-hover/code:opacity-100">
				<CopyButton
					value={formatted}
					tooltip="Copy"
					className="h-7 w-7 bg-gray-800/80 text-gray-300 hover:text-white"
				/>
			</div>
			<pre className="hljs max-h-72 overflow-auto p-4 text-xs leading-relaxed">
				<code
					className="language-json"
					dangerouslySetInnerHTML={{ __html: html }}
				/>
			</pre>
		</div>
	)
}

function DeliveryStatusDot({ status }: { status: string }) {
	// Two failure shades would be ideal (failed = retry pending, dead =
	// terminal) but the Dot component only ships green/red/gray today.
	// Both failure states use red; the status text in the row already
	// disambiguates.
	const color: "green" | "red" | "gray" =
		status === "succeeded"
			? "green"
			: status === "dead" || status === "failed"
				? "red"
				: "gray"
	return <Dot size="sm" color={color} />
}
